import { Injectable, inject } from '@angular/core'
import {
  Auth,
  User,
  MultiFactorResolver,
  PhoneMultiFactorInfo,
  RecaptchaVerifier,
  PhoneMultiFactorGenerator,
  PhoneAuthProvider,
  TotpSecret,
  UserCredential,
} from '@angular/fire/auth'
import { Observable, defer, lastValueFrom, of } from 'rxjs'
import { FIREBASE_AUTH_SDK } from './firebase-auth-sdk'

@Injectable({
  providedIn: 'root',
})
export class MfaService {
  private auth = inject(Auth)
  private sdk = inject(FIREBASE_AUTH_SDK)

  constructor() {}

  async sendSmsCode(
    resolver: MultiFactorResolver,
    hint: PhoneMultiFactorInfo,
  ): Promise<string> {
    const recaptchaVerifier = new RecaptchaVerifier(
      this.auth,
      'recaptcha-container-id',
      undefined,
    )

    try {
      if (hint.factorId === PhoneMultiFactorGenerator.FACTOR_ID) {
        const phoneInfoOptions = {
          multiFactorHint: hint,
          session: resolver.session,
        }

        const phoneAuthProvider = new PhoneAuthProvider(this.auth)

        const verificationId = await phoneAuthProvider.verifyPhoneNumber(
          phoneInfoOptions,
          recaptchaVerifier,
        )

        return verificationId
      } else {
        throw new Error('Phone authentication is not set up for this user')
      }
    } finally {
      recaptchaVerifier.clear()
    }
  }

  async enrollPhoneNumber(user: User, phoneNumber: string): Promise<string> {
    const recaptchaVerifier = new RecaptchaVerifier(
      this.auth,
      'recaptcha-container-id',
      undefined,
    )

    try {
      const multiFactorUser = this.sdk.multiFactor(user)
      const phoneAuthProvider = new PhoneAuthProvider(this.auth)
      const session = await multiFactorUser.getSession()
      const verificationId = await phoneAuthProvider.verifyPhoneNumber(
        {
          phoneNumber: phoneNumber,
          session: session,
        },
        recaptchaVerifier,
      )

      return verificationId
    } finally {
      recaptchaVerifier.clear()
    }
  }

  async verifyCode(
    resolver: MultiFactorResolver,
    verificationId: string,
    verificationCode: string,
  ): Promise<UserCredential> {
    try {
      const credential = PhoneAuthProvider.credential(
        verificationId,
        verificationCode,
      )
      const multiFactorAssertion =
        PhoneMultiFactorGenerator.assertion(credential)
      const userCredential = await resolver.resolveSignIn(multiFactorAssertion)

      await lastValueFrom(this.completeMfaVerification())

      return userCredential
    } catch (error) {
      console.error('SMS verification failed:', error)
      throw error
    }
  }

  async confirmEnrollment(
    user: User,
    verificationId: string,
    verificationCode: string,
  ): Promise<void> {
    try {
      const credential = PhoneAuthProvider.credential(
        verificationId,
        verificationCode,
      )
      const multiFactorAssertion =
        PhoneMultiFactorGenerator.assertion(credential)
      const multiFactorUser = this.sdk.multiFactor(user)
      await multiFactorUser.enroll(multiFactorAssertion, 'Phone Number')

      await lastValueFrom(this.completeMfaVerification())
    } catch (error) {
      console.error('Phone enrollment/update failed:', error)
      throw error
    }
  }

  generateTotpSecret(user: User): Observable<TotpSecret> {
    return defer(async () => {
      const session = await this.sdk.multiFactor(user).getSession()
      return this.sdk.totpMultiFactorGenerator.generateSecret(session)
    })
  }

  enrollTotp(
    user: User,
    secret: TotpSecret,
    code: string,
    displayName: string,
  ): Observable<void> {
    return defer(() => {
      const assertion =
        this.sdk.totpMultiFactorGenerator.assertionForEnrollment(secret, code)
      return this.sdk.multiFactor(user).enroll(assertion, displayName)
    })
  }

  verifyTotpCode(
    resolver: MultiFactorResolver,
    factorUid: string,
    code: string,
  ): Observable<UserCredential> {
    return defer(() => {
      const hint = resolver.hints.find(
        (factor) => factor.uid === factorUid && factor.factorId === 'totp',
      )
      if (!hint)
        throw new Error('TOTP authentication is not set up for this factor')
      const assertion = this.sdk.totpMultiFactorGenerator.assertionForSignIn(
        hint.uid,
        code,
      )
      return resolver.resolveSignIn(assertion)
    })
  }

  private completeMfaVerification(): Observable<{
    success: boolean
  }> {
    return of({
      success: true,
    })
  }
}
